import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await register(name.trim(), email.trim(), password);
      if (user.role === "org_leader" || user.onboarding_state === "complete") {
        toast.success("Welcome! Your organization leader account is ready.");
        navigate("/");
      } else {
        toast.success("Account created — let's set you up.");
        navigate("/onboarding");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F1116] text-[#FFFDF5] relative overflow-hidden">
        <div className="absolute -right-24 top-32 h-72 w-72 rounded-full" style={{ background: "#FF3B7A" }} />
        <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full" style={{ background: "#31B8FF" }} />
        <Logo type="wordmark" variant="white" height={38} />
        <div className="relative z-10">
          <h1 className="text-5xl font-extrabold leading-[1.05] mb-5">
            Start<br /><span style={{ color: "#FF3B7A" }}>growing</span> today.
          </h1>
          <p className="font-reading text-lg opacity-80 max-w-sm">
            Join your organization, pick a mentor, and begin the journey.
          </p>
        </div>
        <p className="relative z-10 text-sm opacity-60">Growing — Next Steps For Students</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm animate-rise" data-testid="register-form">
          <div className="lg:hidden mb-8">
            <Logo type="wordmark" variant="black" height={34} />
          </div>
          <h2 className="text-3xl font-extrabold mb-1">Create account</h2>
          <p className="text-muted-foreground mb-7 text-sm">It only takes a minute.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
                data-testid="register-name" className="mt-1 border-2 border-ink h-12 rounded-xl" placeholder="Jordan Rivers" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="register-email" className="mt-1 border-2 border-ink h-12 rounded-xl" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                data-testid="register-password" className="mt-1 border-2 border-ink h-12 rounded-xl" placeholder="At least 6 characters" />
            </div>
          </div>

          <Button type="submit" disabled={busy} data-testid="register-submit"
            className="w-full mt-6 h-12 rounded-full border-2 border-ink shadow-pop press text-base font-bold">
            {busy ? "Creating…" : "Create account"}
          </Button>

          <p className="text-sm text-center mt-6 text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-foreground underline" data-testid="goto-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
